import * as yup from 'yup';
import dayjs from 'dayjs';
import { CITY, HOTEL } from '../constants/smartfillConstants';

// All below validation functions used `function` over `() =>` as
// we need access to `this` for options & parent.
/**
 * @param {Date} endDate
 * @returns {boolean}
 */
export function validateMinimumNights(endDate) {
  const { MIN_NIGHTS } = this.options.marketConfig;
  const startDate = dayjs(this.parent.startDate);
  const timeEnd = dayjs(endDate);
  const diff = timeEnd.diff(startDate, 'day', true);
  return Math.ceil(diff) >= MIN_NIGHTS;
}

export function validateMaximumNights(endDate) {
  const { MAX_NIGHTS } = this.options.marketConfig;
  const startDate = dayjs(this.parent.startDate);
  const timeEnd = dayjs(endDate);
  const diff = timeEnd.diff(startDate, 'day');
  return diff <= MAX_NIGHTS;
}

export function validateMaximumAdvanceBooking(startDate) {
  const { MAX_ADVANCE_BOOKING } = this.options.marketConfig;
  const startTime = dayjs(startDate);
  const now = dayjs().startOf('day');
  const diff = startTime.diff(now, 'day');
  return diff <= MAX_ADVANCE_BOOKING;
}

export function validateMinimumAdvanceBooking(startDate) {
  const { MIN_ADVANCE_BOOKING } = this.options.marketConfig;
  const minAdvanceBookingDate = dayjs().startOf('day').add(MIN_ADVANCE_BOOKING, 'day');
  const startTime = dayjs(startDate);
  const diff = startTime.diff(minAdvanceBookingDate, 'day');
  return diff >= 0;
}

export function validateTotalTravelerCount(rooms = []) {
  const { MAX_TRAVELERS_CAP } = this.options.marketConfig;
  let travelers = 0;
  rooms.forEach(({ adults = 0, children = [] }) => {
    travelers += adults;
    travelers += children.length;
  });
  return travelers <= MAX_TRAVELERS_CAP;
}

export function validateMaxTravelersPerRoom(rooms = []) {
  const { MAX_TRAVELERS_PER_ROOM } = this.options.marketConfig;
  // eslint-disable-next-line no-restricted-syntax -- pre-existing bypass
  for (const { adults, children = [] } of rooms) {
    if (adults + children.length > MAX_TRAVELERS_PER_ROOM) {
      return false;
    }
  }
  return true;
}

export const validateLocationShape = ({ name, id, type }) => !!(name && id && type)
  || !!([CITY, HOTEL].includes(type) && name && type);

export const validationSchemaWithMarketConfig = (marketConfig) => yup.object().shape({
  location: yup
    .object()
    .test('validate location shape', 'please_enter_a_destination', validateLocationShape),
  startDate: yup
    .date()
    .typeError('please_enter_a_check_in_date')
    .min(dayjs().startOf('day'), 'check_in_date_has_passed')
    .test('validate minimum advance booking less than X days', {
      key: 'we_cannot_book_X_days_less_than',
      value: marketConfig.MIN_ADVANCE_BOOKING,
    }, validateMinimumAdvanceBooking)
    .test('validate maximum advance booking less than X days', {
      key: 'we_cannot_book_X_days_more_than',
      value: marketConfig.MAX_ADVANCE_BOOKING,
    }, validateMaximumAdvanceBooking),
  endDate: yup
    .date()
    .typeError('please_enter_a_check_out_date')
    .min(yup.ref('startDate'), 'checkout_out_date_is_same_as_check_in_date')
    .min(dayjs().startOf('day'), 'check_out_date_has_passed')
    .test('validate minimum X nights', 'check_out_date_is_same_or_before', validateMinimumNights)
    .test('validate maximum of X nights', {
      key: 'we_cannot_book_stays_longer_than_X_days',
      value: marketConfig.MAX_NIGHTS,
    }, validateMaximumNights),
  rooms: yup
    .array()
    .test('validate total travelers per room is less than X', {
      key: 'you_have_selected_more_than_X_room',
      value: marketConfig.MAX_TRAVELERS_PER_ROOM,
    }, validateMaxTravelersPerRoom)
    .test('validate total travelers count is less than X', {
      key: 'you_have_selected_more_than_X_total',
      value: marketConfig.MAX_TRAVELERS_CAP,
    }, validateTotalTravelerCount),
});

export default validationSchemaWithMarketConfig;
